import { useNavigate } from "react-router-dom";
import useRealtime from "../../lib/useRealtime";
import { SpeedGauge, TARGET_BANDS } from "../../components/sc/SpeedGauge";
import { ChevronLeft, Target, Calendar, Users, History, CheckCircle2, Gift } from "lucide-react";

const METRIC_UNIT = { kw: "kW", units: "units" };
const HERO_PHOTO = "https://images.unsplash.com/photo-1655300256335-beef51a914fe?w=700&q=80&auto=format&fit=crop";

function fmt(n) { return Number(n || 0).toLocaleString("en-IN"); }

function CurrentTargetCard({ t }) {
  const unit = METRIC_UNIT[t.metric];
  const isUpcoming = t.status === "upcoming";
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm" data-testid={`sc-target-current-${t.id}`}>
      <div className="relative h-20">
        <img src={HERO_PHOTO} alt="Sales target" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3C1C]/85 via-[#0D4721]/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-3.5 pb-2 flex items-center justify-between">
          <div className="text-white font-bold text-[15px] leading-tight">{t.name}</div>
          <span className={"text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full " + (isUpcoming ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700")}>
            {isUpcoming ? "Upcoming" : "Active"}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-4">
          <SpeedGauge pct={t.progress_pct} label="Group Progress" bands={TARGET_BANDS} testId={`sc-target-gauge-${t.id}`} />
          <div className="flex-1 pl-2 border-l border-slate-100 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.start_date} → {t.end_date}</div>
            <div className="font-mono text-lg font-black text-slate-900 mt-1">{fmt(t.achieved_value)} / {fmt(t.target_value)} {unit}</div>
            <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5"><Users className="w-3 h-3" /> {t.sc_count} SCs covered · {t.days_remaining}d left</div>
          </div>
        </div>
        <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5 flex items-center justify-between" data-testid={`sc-target-contribution-${t.id}`}>
          <div className="text-[11px] font-bold text-emerald-800">Your contribution</div>
          <div className="font-mono text-base font-black text-emerald-700">{fmt(t.my_contribution)} {unit}</div>
        </div>
        {t.incentive_amount > 0 && (
          <div className="mt-2 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2.5 flex items-center gap-2" data-testid={`sc-target-incentive-${t.id}`}>
            <Gift className="w-4 h-4 text-violet-600 flex-shrink-0" />
            <div className="text-[11px] text-violet-800"><b>₹{fmt(t.incentive_amount)}</b> incentive for every SC who contributes — once the group fully hits this target</div>
          </div>
        )}
      </div>
    </div>
  );
}

function HistoricRow({ t }) {
  const unit = METRIC_UNIT[t.metric];
  const hit = t.progress_pct >= 100;
  return (
    <div className="border border-slate-200 rounded-xl p-3.5" data-testid={`sc-target-historic-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-800 truncate">{t.name}</div>
          <div className="text-[11px] font-mono text-slate-400 mt-0.5">{t.start_date} → {t.end_date}</div>
        </div>
        <span className={"flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full " + (hit ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}>
          {hit && <CheckCircle2 className="w-2.5 h-2.5" />} {t.progress_pct}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-2.5">
        <div className={"h-full rounded-full " + (hit ? "bg-emerald-500" : "bg-amber-400")} style={{ width: `${Math.min(100, t.progress_pct)}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px] font-mono text-slate-500">
        <span>Group: {fmt(t.achieved_value)} / {fmt(t.target_value)} {unit}</span>
        <span className="font-bold text-slate-700">You: {fmt(t.my_contribution)} {unit}</span>
      </div>
    </div>
  );
}

export default function ScTarget() {
  const navigate = useNavigate();
  const { data } = useRealtime("/sc/sales-target", { interval: 120000 });
  const current = data?.current || [];
  const historic = data?.historic || [];
  const loaded = !!data;

  return (
    <div data-testid="sc-target-page">
      <button type="button" onClick={() => navigate(-1)} data-testid="sc-target-back-btn"
        className="mb-3 -mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#1B5E20] hover:text-[#15501A] active:scale-95 transition-transform">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2"><Target className="w-5 h-5 text-[#1B5E20]" /> Sales Target</h1>
      <p className="text-xs text-slate-500 mt-1">Your live installation targets and how your group is tracking against them.</p>

      {!loaded && <div className="font-mono text-xs text-slate-400 mt-10 text-center">Loading…</div>}

      {loaded && current.length === 0 && historic.length === 0 && (
        <div className="mt-8 text-center" data-testid="sc-target-empty">
          <img src={HERO_PHOTO} alt="No target yet" className="w-full h-32 object-cover rounded-2xl opacity-80" draggable={false} />
          <div className="text-sm font-bold text-slate-700 mt-4">No target set for you yet</div>
          <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">Your admin hasn't assigned a Sales Target to your region yet. Check back soon!</p>
        </div>
      )}

      {current.length > 0 && (
        <div className="mt-5 space-y-3">
          {current.map((t) => <CurrentTargetCard key={t.id} t={t} />)}
        </div>
      )}

      {historic.length > 0 && (
        <div className="mt-6">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-1.5"><History className="w-3 h-3" /> Historic Record</div>
          <div className="space-y-2.5 pb-6">
            {historic.map((t) => <HistoricRow key={t.id} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
}
